import { type ReactNode } from 'react';
import { Check, Archive, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSwipeGestures } from '@/hooks/useSwipeGestures';

interface SwipeAction {
  icon: LucideIcon;
  color: string;
  bgColor: string;
  label: string;
}

interface SwipeableCardProps {
  children: ReactNode;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  leftAction?: SwipeAction;
  rightAction?: SwipeAction;
  className?: string;
  disabled?: boolean;
}

const defaultLeftAction: SwipeAction = {
  icon: Archive,
  color: 'text-white',
  bgColor: 'bg-warning-500',
  label: 'Archive',
};

const defaultRightAction: SwipeAction = {
  icon: Check,
  color: 'text-white',
  bgColor: 'bg-success-500',
  label: 'Complete',
};

export const SwipeableCard = ({
  children,
  onSwipeLeft,
  onSwipeRight,
  leftAction = defaultLeftAction,
  rightAction = defaultRightAction,
  className,
  disabled = false,
}: SwipeableCardProps) => {
  const { handlers, offset, isSwiping, direction, progress } = useSwipeGestures({
    onSwipeLeft,
    onSwipeRight,
    enabled: !disabled && (!!onSwipeLeft || !!onSwipeRight),
  });

  const LeftIcon = leftAction.icon;
  const RightIcon = rightAction.icon;

  return (
    <div className="relative overflow-hidden rounded-lg">
      {/* Left Action Indicator (shown when swiping right) */}
      {onSwipeRight && (
        <div
          className={cn(
            'absolute inset-y-0 left-0 flex items-center justify-start pl-4',
            'transition-opacity duration-150',
            rightAction.bgColor
          )}
          style={{
            width: Math.max(0, offset),
            opacity: direction === 'right' ? progress : 0,
          }}
        >
          <div className="flex items-center gap-2">
            <RightIcon className={cn('h-5 w-5', rightAction.color)} />
            {progress > 0.7 && (
              <span className={cn('text-sm font-medium', rightAction.color)}>
                {rightAction.label}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Right Action Indicator (shown when swiping left) */}
      {onSwipeLeft && (
        <div
          className={cn(
            'absolute inset-y-0 right-0 flex items-center justify-end pr-4',
            'transition-opacity duration-150',
            leftAction.bgColor
          )}
          style={{
            width: Math.max(0, -offset),
            opacity: direction === 'left' ? progress : 0,
          }}
        >
          <div className="flex items-center gap-2">
            {progress > 0.7 && (
              <span className={cn('text-sm font-medium', leftAction.color)}>
                {leftAction.label}
              </span>
            )}
            <LeftIcon className={cn('h-5 w-5', leftAction.color)} />
          </div>
        </div>
      )}

      {/* Card Content */}
      <div
        {...handlers}
        className={cn(
          'relative bg-white dark:bg-secondary-900',
          'transition-transform',
          !isSwiping && 'duration-200',
          className
        )}
        style={{
          transform: `translateX(${offset}px)`,
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default SwipeableCard;
